import React, { createContext, useContext, useMemo, useCallback, type ReactNode } from 'react';
import type { DashboardStats } from '@/types';
import { MONTHLY_FEE } from '@/lib/payments';
import { useMemberContext } from './MemberContext';
import { usePaymentContext } from './PaymentContext';

interface DashboardContextType {
  getDashboardStats: () => DashboardStats;
}

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export function DashboardProvider({ children }: { children: ReactNode }) {
  const { members } = useMemberContext();
  const { payments } = usePaymentContext();

  const dashboardStats = useMemo((): DashboardStats => {
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();
    
    const monthlyNewMembers = members.filter(member => {
      const joinedDate = new Date(member.joinedDate);
      return joinedDate.getMonth() + 1 === currentMonth && joinedDate.getFullYear() === currentYear;
    }).length;

    const currentMonthPayments = payments.filter(payment => {
      const paymentDate = new Date(payment.paymentDate);
      return paymentDate.getMonth() + 1 === currentMonth && paymentDate.getFullYear() === currentYear;
    });

    const paidMemberIds = new Set(currentMonthPayments.map(p => p.memberId));
    const paidCustomers = paidMemberIds.size;
    const unpaidCustomers = members.length - paidCustomers;

    const receivedAmount = currentMonthPayments.reduce((sum, payment) => sum + payment.amount, 0);
    const expectedAmount = members.length * MONTHLY_FEE;
    const pendingAmount = expectedAmount - receivedAmount;

    const cashReceived = currentMonthPayments
      .filter(p => p.paymentType === 'Cash')
      .reduce((sum, payment) => sum + payment.amount, 0);

    const onlineReceived = currentMonthPayments
      .filter(p => p.paymentType === 'Online')
      .reduce((sum, payment) => sum + payment.amount, 0);

    return {
      totalMembers: members.length,
      monthlyNewMembers,
      unpaidCustomers,
      paidCustomers,
      expectedAmount,
      receivedAmount,
      pendingAmount,
      cashReceived,
      onlineReceived
    };
  }, [members, payments]);

  const getDashboardStats = useCallback((): DashboardStats => {
    return dashboardStats;
  }, [dashboardStats]);

  const contextValue: DashboardContextType = {
    getDashboardStats
  };

  return (
    <DashboardContext.Provider value={contextValue}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboardContext() {
  const context = useContext(DashboardContext);
  if (context === undefined) {
    throw new Error('useDashboardContext must be used within a DashboardProvider');
  }
  return context;
}

